import { useEffect, useState, useRef, useCallback } from "react";
import { debounce } from "lodash";
import Auth from "../../../Auth";
import usePrevious from "../../../hooks/usePrevious";
import { LOAD_ACTION } from "./useCanvasReducer";
import useRefresh from "./useRefresh";
import moment from 'moment';

const loadCanvas = async (projectId) => {
  const results = await Auth.fetchWithIdToken(`/api/canvas/${projectId}/get`, {});

  if (!results.canvas) {
    console.log("couldn't find canvas");
    return;
  }

  return results.canvas;
};

const SEC = 1000;
const MIN = 60 * SEC;
const staleCanvasCheckInterval = 1 * MIN;

export default ({
  // setCards, //
  cards,
  groups,
  projectId,
  position,
  setCanvasPosition,
  setZoom,
  zoom,
  injectSampleCanvas,
  hasInteractedWithCanvas,
  readOnly,
  dispatch,
}) => {
  // const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(moment().toDate());

  const loading = cards === null;

  const prevLoading = usePrevious(loading);

  const canvasIdRef = useRef(null);

  // Loads canvas from backend
  useEffect(() => {
    const run = async () => {
      const canvas = await loadCanvas(projectId);

      if (!canvas) {
        return;
      }

      const { cards: parsedCards, groups: parsedGroups, id } = parseCanvas(canvas);
      canvasIdRef.current = id;

      if (!hasInteractedWithCanvas && !readOnly && Object.keys(parsedCards).length === 0) {
        injectSampleCanvas();
      } else {
        dispatch({ type: LOAD_ACTION, cards: parsedCards, groups: parsedGroups });

        // the order is important here! must set zoom first as position is relative to zoom
        setZoom(canvas.zoom);
        setCanvasPosition(canvas.position);
      }
    };

    run();
  }, []);

  // Valid fields that can persist to backend for all cards
  const FIELDS = [
    "autoHeight",
    "content",
    "createdAt",
    "data",
    "generated",
    "height",
    "width",
    "x",
    "y",
    "type",
    "title",
    "sourceCardIds",
    "placeholder",
    "index",
    "id",
  ];

  /**
   * Formats a card for saving into the backend
   * @param {Object} card
   * @returns
   */
  const formatCard = (card) => {
    const c = {};

    // only copy over valid fields
    for (const field of FIELDS) {
      c[field] = card[field];
    }

    return c;
  };

  /**
   * Unformats a card from the backend into the client
   * @param {Object} card
   * @returns
   */
  const unformatCard = (card) => {
    const c = { ...card };
    return Object.assign(c, {
      selected: false,
      editable: true,
      generating: false,
      defaultPosition: {
        x: c.x,
        y: c.y,
      },
    });
  };

  const parseCanvas = useCallback((canvas) => {
    const cardsToSet = {};
    let groupsToSet = {};

    Object.keys(canvas.cards).forEach((id) => {
      cardsToSet[id] = unformatCard(canvas.cards[id]);
    });

    if (canvas.groups) {
      groupsToSet = canvas.groups;
    }

    const id = canvas.id;

    return { cards: cardsToSet, groups: groupsToSet, id };
  }, []);

  const checkForUpdates = useCallback(async () => {
    const canvas = await loadCanvas(projectId);

    if (!canvas) {
      return;
    }

    const { updatedAt: { _seconds: updatedAtInSecs } } = canvas;
    const contentMismatch = moment.unix(updatedAtInSecs).isAfter(moment(lastSavedAt));
    if (saved && contentMismatch) {
      document.location.reload();
    }
  }, [lastSavedAt, projectId, saved]);

  useRefresh({
    refresh: checkForUpdates,
    periodicRefreshInterval: staleCanvasCheckInterval,
  });

  /**
   * Main save call
   */
  const saveCanvas = useRef(
    debounce(({ cards, groups, position, zoom }) => {
      if (canvasIdRef.current) {
        const cardsToSave = {};

        Object.keys(cards).forEach((id) => {
          const card = cards[id];
          cardsToSave[id] = formatCard(card);
        });

        Auth.fetchWithIdToken(`/api/canvas/${canvasIdRef.current}`, {
          method: "PUT",
          cards: cardsToSave,
          groups: groups,
          position: position,
          zoom: zoom,
        })
          .then(() => {
            setSaved(true);
            setError(false);
            setLastSavedAt(new Date());
          })
          .catch((e) => {
            // error message
            console.log("save failed!!");
            setError("We are having trouble saving changes. Please check your internet connection to avoid losing work.");

            // attempt save again
            setTimeout(() => {
              saveCanvas.current({ cards, groups, position, zoom });
            }, 5000);
          });
      }
    }, 1000)
  );

  // save cards
  useEffect(() => {
    if (readOnly) {
      return;
    }

    if (cards === null || prevLoading) {
      // loading or loaded first time, so we don't need to save
      return;
    }

    setSaved(false);
    saveCanvas.current({ cards, groups, position, zoom });
  }, [cards, groups, position, zoom, readOnly]);

  return { loading, saved, canvasIdRef, error };
};
